import {EventEmitter, Injectable} from '@angular/core';
import {MapViewMode}              from '../../ap-interface';
import {MapViewStore}             from '../../stores/layout/mapview.store';
import {MapStore}                 from '../../stores/map/map.store';
import {MapFactoryLayer}          from '../../ap-map/layers/map-factory-layer';
import {MapFactoryStyler}         from '../../map-factory/style';
import IGuid = System.IGuid;

@Injectable({
  providedIn: 'root'
})
export class ApShowPointsOnMapService {
  onMapClear = new EventEmitter();
  private _pointMap: { [key: string]: { datasetId: string, fieldGeomId: string } } = {};
  private _rasterMap: { [key: string]: string } = {};
  private _rasterReset = true;
  private _band = 0;

  constructor(private mapStore: MapStore,
              private mapViewStore: MapViewStore) {
    this.mapStore.Layers.NRaster.legend().selectionLists[0].selectedValue.subscribe((val) => {
      this._band = +val.value;
      this.mapStore.Mutate(s => s.layer.nRasterUrl, () =>
        `/map/pro_nd/map_nd/{z}/{x}/{y}?params=${JSON.stringify(Object.keys(this._rasterMap).map((key) => this._rasterMap[key]))}#${this._band}`
      );
    });
    this.mapStore.Layers.PpRaster.legend().selectionLists[0].selectedValue.subscribe((val) => {
      this._band = +val.value;
      this.mapStore.Mutate(s => s.layer.ppRasterUrl, () =>
        `/map/pro_pp/map_pp/{z}/{x}/{y}?params=${JSON.stringify(Object.keys(this._rasterMap).map((key) => this._rasterMap[key]))}#${this._band}`
      );
    });
  }

  showPoints(selectedItems: { DatasetId: string, FieldGeomId: string }[], farmId: number): void {
    this._pointMap = {};
    for (const d of selectedItems) {
      this._collectPoints(d.DatasetId, d.FieldGeomId);
    }

    let url = '';
    const values = Object.values(this._pointMap).map((v) => {
      if (v.fieldGeomId == null) {
        delete v.fieldGeomId;
      }
      return v;
    });

    url = `/map/pro_n_sensor_log_points/map_n_sensor_log_points/{z}/{x}/{y}?params=${JSON.stringify(values)}#${farmId}`;
    this.mapStore.Mutate(s => s.layer.nSensorLogPointsUrl, () => url);
  }

  showAllRaster(data: { FieldId: string, PlanBookId: string }[], type: 'nd' | 'pp'): void {
    this._rasterReset = true;
    this._rasterMap = {};
    data.forEach((d) => this._rasterMap[d.FieldId] = d.PlanBookId);
    this._showRaster(type);
  }

  showRaster(fieldId: string, planId: string, type: 'nd' | 'pp'): void {
    if (this._rasterReset) {
      this._rasterMap = {};
      this._rasterReset = false;
    }
    if (this._rasterMap[fieldId] === planId) {
      delete this._rasterMap[fieldId];
    } else {
      this._rasterMap = {};
      this._rasterMap[fieldId] = planId;
    }
    this._showRaster(type);
  }

  isRasterShown(fieldId: IGuid | string, planId: IGuid | string): boolean {
    return this._rasterMap[fieldId as string] === planId as string;
  }

  private _collectPoints(datasetId: string, fieldGeomId: string): void {
    const id = `${datasetId}-${fieldGeomId}`;
    if (this.mapViewStore.getMapViewMode() !== MapViewMode.NORMAL) {
      this.mapViewStore.showMapView();
    }
    if (this._pointMap.hasOwnProperty(id)) {
      this._removeId(id);
    } else {
      if (!this.mapStore.Layers.SensorPoints.Visibility) {
        this._toggleLayer(this.mapStore.Layers.SensorPoints, true);
      }
      this._pointMap[id] = {datasetId, fieldGeomId};
    }
  }

  private _removeId(id: string | IGuid): void {
    delete this._pointMap[id as string];
    if (Object.keys(this._pointMap).length === 0) {
      this.onMapClear.emit();
      this._toggleLayer(this.mapStore.Layers.SensorPoints, false);
    }
  }

  isActive(datasetId: string, fieldGeomId: string): boolean {
    return Object.keys(this._pointMap).includes(`${datasetId}-${fieldGeomId}`);
  }

  clear(): void {
    this._pointMap = {};
    this._rasterMap = {};
    this.mapStore.Mutate(s => s.layer.nSensorLogPointsUrl, () => ``);
    this.mapStore.Layers.SensorPoints.clear();
    this.mapStore.Mutate(s => s.layer.nRasterUrl, () => ``);
    this.mapStore.Layers.NRaster.clear();
    this.mapStore.Mutate(s => s.layer.ppRasterUrl, () => ``);
    this.mapStore.Layers.PpRaster.clear();
  }

  private _toggleLayer(layer: MapFactoryLayer, visibility: boolean): void {
    layer.Visibility = visibility;
    this.mapStore.Layers.OnLayerChange.emit();
  }

  private _showRaster(type: 'nd' | 'pp'): void {
    const values = Object.keys(this._rasterMap).map((key) => this._rasterMap[key]);
    if (this.mapViewStore.getMapViewMode() !== MapViewMode.NORMAL) {
      this.mapViewStore.showMapView();
    }
    MapFactoryStyler.Instance.clearStyleCache();
    switch (type) {
      case 'nd':
        if (!this.mapStore.Layers.NRaster.Visibility) {
          this._toggleLayer(this.mapStore.Layers.NRaster, true);
        }
        this.mapStore.Mutate(s => s.layer.nRasterUrl, () =>
          `/map/pro_nd/map_nd/{z}/{x}/{y}?params=${JSON.stringify(values)}#${this._band}`
        );
        break;
      case 'pp':
        if (!this.mapStore.Layers.PpRaster.Visibility) {
          this._toggleLayer(this.mapStore.Layers.PpRaster, true);
        }
        this.mapStore.Mutate(s => s.layer.ppRasterUrl, () =>
          `/map/pro_pp/map_pp/{z}/{x}/{y}?params=${JSON.stringify(values)}#${this._band}`
        );
        break;
    }
  }
}
